import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from '@arco-design/web-react';
import { adminUpstreamCommApi } from '@/api';
import type { PaginatedResult } from '@/types/api';
import type {
  UpstreamCommLogDetail,
  UpstreamCommLogItem,
  UpstreamCommLogQuery,
  UpstreamCommResult,
} from '@/types/upstreamCommLog';

const RESULT_COLOR: Record<string, string> = {
  SUCCESS: 'green',
  UPSTREAM_FAIL: 'gold',
  TIMEOUT: 'orange',
  HTTP_ERROR: 'red',
  HTTP_STATUS: 'red',
  JSON_INVALID: 'magenta',
};

const RESULT_TYPES: UpstreamCommResult[] = [
  'SUCCESS',
  'UPSTREAM_FAIL',
  'TIMEOUT',
  'HTTP_ERROR',
  'HTTP_STATUS',
  'JSON_INVALID',
];

export default function UpstreamCommLogsView() {
  const [traceEnabled, setTraceEnabled] = useState(false);
  const [traceLoading, setTraceLoading] = useState(true);
  const [traceSaving, setTraceSaving] = useState(false);
  const [data, setData] = useState<PaginatedResult<UpstreamCommLogItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<UpstreamCommLogQuery>({ page: 1, pageSize: 20 });
  const [detail, setDetail] = useState<UpstreamCommLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadSetting = useCallback(() => {
    setTraceLoading(true);
    adminUpstreamCommApi
      .getTraceSetting()
      .then((s) => setTraceEnabled(s.enabled))
      .catch(() => null)
      .finally(() => setTraceLoading(false));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    adminUpstreamCommApi
      .listLogs(query)
      .then(setData)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    loadSetting();
  }, [loadSetting]);

  useEffect(() => {
    load();
  }, [load]);

  const onToggleTrace = async (checked: boolean) => {
    setTraceSaving(true);
    try {
      const s = await adminUpstreamCommApi.updateTraceSetting(checked);
      setTraceEnabled(s.enabled);
    } finally {
      setTraceSaving(false);
    }
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const d = await adminUpstreamCommApi.logDetail(id);
      setDetail(d);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">上游通讯调试</div>
          <div className="admin-page__subtitle">
            开启后记录所有 Newgen 上游请求与响应，便于联调；关闭后不再写入新记录
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ marginBottom: 16 }}>
        <Space align="center" size="large">
          <Typography.Text>全量通讯记录</Typography.Text>
          <Switch
            checked={traceEnabled}
            loading={traceLoading || traceSaving}
            onChange={onToggleTrace}
          />
          <Typography.Text type="secondary">
            {traceEnabled ? '已开启：每次上游调用均会落库' : '已关闭：不产生新的通讯日志'}
          </Typography.Text>
        </Space>
        {!traceEnabled && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            content="开关关闭时不会记录新的通讯；下方列表为历史已写入的记录。"
          />
        )}
      </div>

      <div className="admin-card">
        <Form
          layout="inline"
          style={{ marginBottom: 12 }}
          initialValues={query}
          onSubmit={(values) => setQuery({ ...query, ...values, page: 1 })}
        >
          <Form.Item field="apiPath" label="接口路径">
            <Input placeholder="/virtualcard/..." allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item field="result" label="结果">
            <Select placeholder="全部" allowClear style={{ width: 160 }}>
              {RESULT_TYPES.map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item field="keyword" label="单号/卡 ID">
            <Input placeholder="out_trade_no / card_id" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button onClick={() => setQuery({ page: 1, pageSize: 20 })}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table<UpstreamCommLogItem>
          loading={loading}
          rowKey="id"
          data={data?.items ?? []}
          pagination={{
            current: query.page ?? 1,
            pageSize: query.pageSize ?? 20,
            total: data?.total ?? 0,
            onChange: (p) => setQuery({ ...query, page: p }),
            showTotal: true,
          }}
          columns={[
            { title: '时间', dataIndex: 'createdAt', width: 180 },
            {
              title: '接口',
              dataIndex: 'apiPath',
              render: (v) => <span className="admin-mono">{v}</span>,
            },
            {
              title: '结果',
              dataIndex: 'result',
              width: 130,
              render: (v: string) => (
                <Tag color={RESULT_COLOR[v] ?? 'gray'}>{v}</Tag>
              ),
            },
            {
              title: 'HTTP',
              dataIndex: 'httpStatus',
              width: 72,
              align: 'right',
              render: (v) => (v != null ? v : '-'),
            },
            {
              title: '耗时(ms)',
              dataIndex: 'durationMs',
              width: 88,
              align: 'right',
              render: (v) => (v != null ? v : '-'),
            },
            {
              title: '商户单号',
              dataIndex: 'outTradeNo',
              width: 160,
              render: (v) => v || '-',
            },
            {
              title: '卡 ID',
              dataIndex: 'upstreamCardId',
              width: 120,
              render: (v) =>
                v ? <span className="admin-mono">{v}</span> : '-',
            },
            {
              title: '操作',
              dataIndex: 'op',
              width: 88,
              render: (_, row) => (
                <Button size="mini" onClick={() => openDetail(row.id)}>
                  详情
                </Button>
              ),
            },
          ]}
        />
      </div>

      <Drawer
        title="上游通讯详情"
        width={720}
        visible={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
      >
        {detailLoading || !detail ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              <Tag color={RESULT_COLOR[detail.result] ?? 'gray'}>{detail.result}</Tag>{' '}
              <span className="admin-mono">{detail.apiPath}</span>
            </div>
            <Typography.Text type="secondary">
              时间：{detail.createdAt}
              {detail.httpStatus != null ? ` · HTTP ${detail.httpStatus}` : ''}
              {detail.durationMs != null ? ` · ${detail.durationMs} ms` : ''}
            </Typography.Text>
            {(detail.outTradeNo || detail.upstreamCardId) && (
              <Typography.Text type="secondary">
                {detail.outTradeNo ? `商户单号：${detail.outTradeNo}` : ''}
                {detail.outTradeNo && detail.upstreamCardId ? ' · ' : ''}
                {detail.upstreamCardId ? `卡 ID：${detail.upstreamCardId}` : ''}
              </Typography.Text>
            )}
            {detail.requestParams != null && (
              <div>
                <div style={{ marginBottom: 4, color: 'var(--admin-text-tertiary)' }}>
                  请求参数：
                </div>
                <pre
                  style={{
                    background: 'var(--admin-bg-page)',
                    padding: 12,
                    borderRadius: 6,
                    margin: 0,
                    maxHeight: 240,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(detail.requestParams, null, 2)}
                </pre>
              </div>
            )}
            {detail.responseBody && (
              <div>
                <div style={{ marginBottom: 4, color: 'var(--admin-text-tertiary)' }}>
                  响应内容：
                </div>
                <pre
                  style={{
                    background: 'var(--admin-bg-page)',
                    padding: 12,
                    borderRadius: 6,
                    margin: 0,
                    maxHeight: 360,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {detail.responseBody}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
}
